import Avatar from "@components/avatar";
import { IGetValueArgs } from "@components/smart/FieldInfo";
import { TCellValue } from "@components/table";
import { ICompanyEntity } from "@odata/GeneratedEntityTypes";
import { CountryCode, CurrencyCode, VatStatementFrequencyCode, VatStatusCode } from "@odata/GeneratedEnums";
import { CompanyAvatarName, CompanyAvatarWithNameWrapper } from "@pages/companies/Company.styles";
import { VatStatementPeriod, VatStatementPeriodUnit } from "@pages/companies/Company.types";
import { getCompanyCurrency, getCompanyLogoUrl, getCompanyVatStatuses, TVatStatusEntity } from "@utils/CompanyUtils";
import dayjs, { Dayjs } from "dayjs";
import React from "react";

import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { AvatarSize } from "../../enums";
import { getUtcDayjs } from "../../types/Date";

export const companyIsSecondaryAddressPath = "##IsSecondaryAddress##";

/**
 * Returns current agenda country code with optional fallback value
 * @param context
 * @param defVal
 */
export function getAgendaCountryCode(context: IAppContext, defVal = CountryCode.CzechRepublic): CountryCode {
    return (context.getCompany()?.Accounting?.CountryCode ?? defVal) as CountryCode;
}

/** This list includes even company currency (CZK), even though currenciesUsedByCompany doesn't have it */
export function getCompanyUsedCurrencyCodes(context: IAppContext): CurrencyCode[] {
    const currencies = [...(context.getData().currenciesUsedByCompany?.map(c => c.FromCurrencyCode) ?? [])] as CurrencyCode[];
    currencies.push(getCompanyCurrency(context));
    return currencies;
}

export function createVatStatementPeriod(date: Dayjs, unit: VatStatementPeriodUnit): VatStatementPeriod {
    return {
        from: date.startOf(unit).startOf("day"),
        to: date.endOf(unit).endOf("day")
    };
}

export function getDayjsUnit(frequency: VatStatementFrequencyCode): VatStatementPeriodUnit {
    return frequency === VatStatementFrequencyCode.Quarterly ? "quarter" : "month";
}

function getCompanyVatStatusesValidInRange(context: IAppContext, date?: Date, unit: dayjs.OpUnitType = "year"): TVatStatusEntity[] {
    const dayjsDate = getUtcDayjs(date);
    const rangeFrom = dayjsDate.startOf(unit);
    const rangeTo = dayjsDate.endOf(unit);
    const vatStatuses = getCompanyVatStatuses(context.getCompany());
    return vatStatuses.filter(vatStatus => {
        const from = getUtcDayjs(vatStatus.DateValidFrom);
        const to = getUtcDayjs(vatStatus.DateValidTo);
        return from.isSameOrBefore(rangeTo) && to.isSameOrAfter(rangeFrom);
    });
}

function getCompanyVatStatementFrequencyCode(context: IAppContext, date?: Date): VatStatementFrequencyCode {
    const vatPayerStatusInRange = getCompanyVatStatusesValidInRange(context, date)
            .find(status => status.VatStatusCode === VatStatusCode.VATRegistered);

    return vatPayerStatusInRange?.VatStatementFrequencyCode as VatStatementFrequencyCode;
}

/**
 *
 * @param context
 * @param forDate
 * @param add possibility to get next/previous period
 */
export function getCompanyVatStatementPeriod(context: IAppContext, forDate: Date, add = 0): VatStatementPeriod {
    const frequency = getCompanyVatStatementFrequencyCode(context);
    const unit = getDayjsUnit(frequency);
    const periodDate = getUtcDayjs(forDate).add(add, unit);
    return createVatStatementPeriod(periodDate, unit);
}

const getCompanyAvatarWithName = (company: ICompanyEntity): React.ReactElement => {
    if (!company) {
        return null;
    }

    const logoSrc = getCompanyLogoUrl(company);

    return (
            <CompanyAvatarWithNameWrapper>
                <Avatar src={logoSrc} size={AvatarSize.S}/>
                <CompanyAvatarName>
                    {company.Name.toUpperCase()}
                </CompanyAvatarName>
            </CompanyAvatarWithNameWrapper>
    );
};

export const companyCellFormatter = (company: ICompanyEntity): TCellValue => {
    if (!company) {
        return null;
    }

    return {
        value: getCompanyAvatarWithName(company),
        tooltip: company.Name,
        onlyShowTooltipWhenChildrenOverflowing: false
    };
};

export const isSecondaryAddressVisible = (args: IGetValueArgs): boolean => {
    return !!args.storage.getValueByPath(companyIsSecondaryAddressPath);
};